import React from 'react';
import { X, FileText, Brain, Zap, Shield } from 'lucide-react';

const LearnMoreModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-[#111] rounded-[20px] border border-gray-200 dark:border-white/[0.06] shadow-2xl text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-6 pt-6 pb-4">
            <div className="w-full">
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100 dark:border-white/[0.06]">
                <h3 className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                  About File Processing & AI Training
                </h3>
                <button
                  onClick={onClose}
                  className="p-1.5 text-gray-400 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-[10px] transition-colors focus:outline-none"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-[10px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1.5">
                      Document Processing
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-500 font-light leading-relaxed">
                      PaperBrainz extracts and processes text from your PDFs, DOC, DOCX, and TXT files.
                      Our AI analyzes the content to understand context, structure, and key information
                      that will help train your customer service chatbot.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-[10px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center">
                    <Brain className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1.5">
                      AI Training
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-500 font-light leading-relaxed">
                      Your uploaded documents become part of your AI's knowledge base. The system
                      learns from your FAQs, support guidelines, product catalogs, and customer
                      conversations to provide accurate, contextual responses.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-[10px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center">
                    <Zap className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1.5">
                      Smart Processing
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-500 font-light leading-relaxed">
                      Documents are automatically categorized and indexed. The AI identifies
                      key topics, procedures, and frequently asked questions to improve response
                      accuracy and reduce training time.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-[10px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center">
                    <Shield className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1.5">
                      Secure & Private
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-500 font-light leading-relaxed">
                      Your documents are processed securely with enterprise-grade encryption.
                      Data is never shared with third parties and remains within your
                      organization's private AI instance.
                    </p>
                  </div>
                </div>

                <div className="rounded-[12px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Supported File Types
                  </h4>
                  <ul className="text-sm text-gray-500 dark:text-gray-500 font-light space-y-1">
                    <li>• PDF documents (.pdf)</li>
                    <li>• Microsoft Word (.doc, .docx)</li>
                    <li>• Plain text files (.txt)</li>
                    <li>• Markdown files (.md)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 dark:border-white/[0.06] flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-full bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 font-medium py-2.5 px-6 text-sm transition-colors duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnMoreModal;